import { ContactEditForm } from '@agentic-flow-studio/shared/components/contacts/ContactEditForm';
import { ContactsFilterBar } from '@agentic-flow-studio/shared/components/contacts/ContactsFilterBar';
import { ContactsTable } from '@agentic-flow-studio/shared/components/contacts/ContactsTable';
import { CreateContactDialog } from '@agentic-flow-studio/shared/components/contacts/CreateContactDialog';
import { SharedContactPage } from '@agentic-flow-studio/shared/components/contacts/SharedContactPage';
import { EmptyState } from '@agentic-flow-studio/shared/components/empty-state';
import { useContactsSearch } from '@agentic-flow-studio/shared/hooks/useContactsSearch';
import { useRecordRecentPage } from '@agentic-flow-studio/shared/hooks/useRecordRecentPage';
import { useSearchPreservingNavigate } from '@agentic-flow-studio/shared/hooks/useSearchPreservingNavigate';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@agentic-flow-studio/shared/ui/dropdown-menu';
import { ArrowLeft, Loader2, MoreVertical, Plus, Search, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '@/utils/trpc';

export default function ContactsPage() {
  const navigate = useSearchPreservingNavigate();
  const location = useLocation();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Parse contact id and edit mode from the pathname.
  const editMatch = location.pathname.match(/\/contacts\/edit\/(\d+)/);
  const viewMatch = location.pathname.match(/\/contacts\/(\d+)/);
  const parsedContactId = editMatch
    ? parseInt(editMatch[1], 10)
    : viewMatch
      ? parseInt(viewMatch[1], 10)
      : undefined;
  const isEditMode = !!editMatch;

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const contactsSearch = useContactsSearch();
  const utils = trpc.useUtils();

  const contactQuery = trpc.contacts.getById.useQuery(
    { id: parsedContactId! },
    { enabled: parsedContactId !== undefined }
  );

  const deleteMutation = trpc.contacts.delete.useMutation({
    onSuccess: () => {
      toast.success('Contact deleted');
      utils.contacts.search.invalidate();
      navigate(`/org/${orgId}/workspace/${workspaceId}/contacts`);
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const updateMutation = trpc.contacts.update.useMutation({
    onSuccess: () => {
      toast.success('Contact updated');
      utils.contacts.search.invalidate();
      contactQuery.refetch();
      if (parsedContactId) {
        navigate(`/org/${orgId}/workspace/${workspaceId}/contacts/${parsedContactId}`);
      } else {
        navigate(`/org/${orgId}/workspace/${workspaceId}/contacts`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const handleDelete = (id: number) => {
    if (isReadOnly) return;
    if (confirm('Are you sure you want to delete this contact? This action cannot be undone.')) {
      deleteMutation.mutate({ id });
    }
  };

  // Must sit above the early return below — this page swaps to a full-page contact view rather
  // than rendering a separate route component, so the hook has to run on the list render too.
  const viewedContact = contactQuery.data;
  useRecordRecentPage(
    viewedContact && parsedContactId !== undefined
      ? {
          key: `contact:${viewedContact.id}`,
          type: 'contact',
          label: viewedContact.displayName || viewedContact.label,
          description: viewedContact.jobTitle ?? viewedContact.companyName ?? undefined,
          avatarUrl: viewedContact.avatarUrl ?? undefined,
        }
      : null
  );

  // ── Full Page Contact View / Edit Mode when viewing/editing (:contactId) ───
  if (parsedContactId !== undefined) {
    if (contactQuery.isLoading) {
      return (
        <div className="flex h-full w-full items-center justify-center p-8 bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (isEditMode && contactQuery.data) {
      return (
        <div className="flex flex-col h-full w-full bg-background">
          <div className="px-6 py-2.5 border-b border-border flex items-center justify-between shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`/org/${orgId}/workspace/${workspaceId}/contacts/${parsedContactId}`)
              }
              className="gap-2 text-xs font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Cancel Editing & Return to Contact Page
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 scrollbar-thin">
            <ContactEditForm
              contact={contactQuery.data}
              isSubmitting={updateMutation.isPending}
              isDeleting={deleteMutation.isPending}
              onDelete={() => handleDelete(parsedContactId)}
              onCancel={() =>
                navigate(`/org/${orgId}/workspace/${workspaceId}/contacts/${parsedContactId}`)
              }
              onSubmit={(data) => {
                updateMutation.mutate({
                  id: parsedContactId,
                  label: data.label,
                  typeId: data.typeId,
                  content: data.content,
                  metadata: data.metadata,
                  displayName: data.displayName,
                  avatarUrl: data.avatarUrl ?? null,
                  prefix: data.prefix,
                  firstName: data.firstName,
                  middleName: data.middleName,
                  lastName: data.lastName,
                  suffix: data.suffix,
                  jobTitle: data.jobTitle,
                  companyName: data.companyName,
                  website: data.website,
                  ownerId: data.ownerId,
                  addressStreet: data.addressStreet,
                  addressCity: data.addressCity,
                  addressState: data.addressState,
                  addressPostalCode: data.addressPostalCode,
                  addressCountry: data.addressCountry,
                  notes: data.notes,
                  emails: data.emails,
                  phones: data.phones,
                  filterSlot1: data.filterSlot1,
                  filterSlot2: data.filterSlot2,
                  filterSlot3: data.filterSlot3,
                  filterSlot4: data.filterSlot4,
                  filterSlot5: data.filterSlot5,
                  filterSlot6: data.filterSlot6,
                  filterSlot7: data.filterSlot7,
                  filterSlot8: data.filterSlot8,
                  filterSlot9: data.filterSlot9,
                  filterSlot10: data.filterSlot10,
                  filterSlot11: data.filterSlot11,
                  filterSlot12: data.filterSlot12,
                  filterSlot13: data.filterSlot13,
                  filterSlot14: data.filterSlot14,
                  filterSlot15: data.filterSlot15,
                  filterSlot16: data.filterSlot16,
                  filterSlot17: data.filterSlot17,
                  filterSlot18: data.filterSlot18,
                  filterSlot19: data.filterSlot19,
                  filterSlot20: data.filterSlot20,
                  fileAssociations: data.fileAssociations,
                });
              }}
              isReadOnly={isReadOnly}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full w-full bg-background">
        {/* Top Breadcrumb Navigation */}
        <div className="px-6 py-2.5 border-b border-border bg-muted flex items-center justify-between shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/org/${orgId}/workspace/${workspaceId}/contacts`)}
            className="text-muted-foreground hover:text-foreground hover:bg-accent gap-2 text-xs font-medium"
          >
            <ArrowLeft className="w-4 h-4 text-primary" />
            Back to Contacts Directory
          </Button>
        </div>

        {/* Dedicated Full Contact Page Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <SharedContactPage
            appKey="inbox-clinic"
            contact={contactQuery.data}
            onClose={() => navigate(`/org/${orgId}/workspace/${workspaceId}/contacts`)}
            onEditContact={() => {
              navigate(`/org/${orgId}/workspace/${workspaceId}/contacts/edit/${parsedContactId}`);
            }}
            onSendEmail={(email) => {
              if (email) window.location.href = `mailto:${email}`;
            }}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    );
  }

  // ── Contacts Directory Table (List View) ───────────────────────────────────
  return (
    <main className="flex-1 p-6 lg:p-10 space-y-8 bg-muted overflow-y-auto h-full scrollbar-thin">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="display font-display text-3xl font-light tracking-tight">Contacts</h1>
          <p className="text-muted-foreground mt-1">
            Everyone linked to your inbox — shared with Anchor Portal across the platform.
          </p>
        </div>
        {!isReadOnly && (
          <Button className="w-full md:w-auto" onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Contact
          </Button>
        )}
      </div>

      <CreateContactDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={(id) => navigate(`/org/${orgId}/workspace/${workspaceId}/contacts/edit/${id}`)}
      />

      {!contactsSearch.isLoading &&
      contactsSearch.totalCount === 0 &&
      !contactsSearch.hasActiveFilters ? (
        <EmptyState
          icon={Users}
          title="No contacts yet"
          description="Add contacts and customers to triage emails, assign tasks, and manage relationships."
          action={
            !isReadOnly ? (
              <Button size="sm" onClick={() => setIsCreateOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" />
                <span>Create Contact</span>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <ContactsFilterBar
            searchInput={contactsSearch.searchInput}
            onSearchInputChange={contactsSearch.setSearchInput}
            filters={contactsSearch.filters}
            onSetFilter={contactsSearch.setFilter}
            onResetFilters={contactsSearch.resetFilters}
          />

          {!contactsSearch.isLoading &&
          contactsSearch.totalCount === 0 &&
          contactsSearch.hasActiveFilters ? (
            <EmptyState
              card
              icon={Search}
              title="No contacts match your search"
              description="Try adjusting your search terms or clearing your filters to view contacts."
              action={{
                label: 'Clear Filters',
                variant: 'outline',
                onClick: contactsSearch.resetFilters,
              }}
            />
          ) : (
            <ContactsTable
              items={contactsSearch.items}
              isLoading={contactsSearch.isLoading}
              selectedId={parsedContactId}
              onRowClick={(id) => navigate(`/org/${orgId}/workspace/${workspaceId}/contacts/${id}`)}
              emptyLabel="No contacts found"
              pageStart={contactsSearch.pageStart}
              pageEnd={contactsSearch.pageEnd}
              totalCount={contactsSearch.totalCount}
              hasNextPage={contactsSearch.hasNextPage}
              hasPrevPage={contactsSearch.hasPrevPage}
              onNextPage={contactsSearch.goToNextPage}
              onPrevPage={contactsSearch.goToPrevPage}
              renderRowActions={
                isReadOnly
                  ? undefined
                  : (contact) => (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/org/${orgId}/workspace/${workspaceId}/contacts/${contact.id}`
                              )
                            }
                          >
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/org/${orgId}/workspace/${workspaceId}/contacts/edit/${contact.id}`
                              )
                            }
                          >
                            Edit Contact
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
                            onClick={() => handleDelete(contact.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
              }
            />
          )}
        </>
      )}
    </main>
  );
}
